import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Play,
  Users,
  BarChart3,
  DollarSign,
  TrendingUp,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  ChevronRight,
  FileText,
  Sparkles,
  Target,
  Award,
} from 'lucide-react';

const LandingPage = () => {
  // Animated counter for stats
  const [stats, setStats] = useState({ companies: 0, deals: 0, commission: 0 });
  
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        companies: prev.companies < 150 ? prev.companies + 3 : 150,
        deals: prev.deals < 12500 ? prev.deals + 250 : 12500,
        commission: prev.commission < 45 ? prev.commission + 1 : 45,
      }));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Lead Management',
      description: 'Capture, track, and convert solar leads with our intuitive pipeline system.',
    },
    {
      icon: TrendingUp,
      title: '10-Stage Pipeline',
      description: 'From lead capture to PTO, track every deal through your custom sales process.',
    },
    {
      icon: DollarSign,
      title: 'Commission Tracking',
      description: 'Automated commission calculations for brokers, managers, and directors.',
    },
    {
      icon: BarChart3,
      title: 'Real-Time Reports',
      description: 'Instant insights into team performance, payouts, and revenue.',
    },
    {
      icon: Shield,
      title: 'Team Management',
      description: 'Roles and permissions for admins, reps, back office, and more.',
    },
    {
      icon: FileText,
      title: 'Document Storage',
      description: 'Upload proposals, contracts, utility bills, and project designs.',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Sign Up in 60 Seconds',
      description: 'Create your company account with a 14-day free trial. No credit card required.',
    },
    {
      step: '02',
      title: 'Add Your Team',
      description: 'Invite sales reps, managers, and back office staff. Set commission tiers.',
    },
    {
      step: '03',
      title: 'Start Closing Deals',
      description: 'Capture leads, track progress, and watch commissions calculate automatically.',
    },
  ];

  const [activeVideo, setActiveVideo] = useState(0);

  // Benefits for Why Choose Us section
  const benefits = [
    {
      title: 'Built for Solar',
      description: 'Unlike generic CRMs, every feature is designed specifically for solar sales workflows.',
      icon: Target,
    },
    {
      title: 'Transparent Commissions',
      description: 'Automated calculations mean no more spreadsheet errors or payment disputes.',
      icon: DollarSign,
    },
    {
      title: 'Quick Setup',
      description: 'Get your team up and running in minutes, not weeks. No IT department needed.',
      icon: Zap,
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Solar Moguls" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-xl font-bold text-slate-800">Solar Moguls</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600 hover:text-slate-800 hover:bg-slate-100">
                Sign In
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                Start Free Trial
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-2 mb-6 font-medium">
              <Sparkles className="w-4 h-4 mr-2" />
              The #1 CRM for Solar Sales Teams
            </Badge>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-slate-900">
              Close More Solar Deals.
              <br />
              <span className="text-blue-600">
                Track Every Commission.
              </span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              The all-in-one platform built for solar sales teams. Manage leads, track commissions, 
              and grow your business with powerful automation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/signup">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg h-auto shadow-lg shadow-blue-600/25">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Your Free 14-Day Trial
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg h-auto"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                Watch Demo
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              <div className="text-center p-6 rounded-2xl bg-white shadow-md border border-slate-100">
                <p className="text-4xl font-bold text-blue-600 font-mono">{stats.companies}+</p>
                <p className="text-slate-500 mt-1 font-medium">Solar Companies</p>
              </div>
              <div className="text-center p-6 rounded-2xl bg-white shadow-md border border-slate-100">
                <p className="text-4xl font-bold text-teal-600 font-mono">{stats.deals.toLocaleString()}+</p>
                <p className="text-slate-500 mt-1 font-medium">Deals Tracked</p>
              </div>
              <div className="text-center p-6 rounded-2xl bg-white shadow-md border border-slate-100">
                <p className="text-4xl font-bold text-amber-600 font-mono">${stats.commission}M+</p>
                <p className="text-slate-500 mt-1 font-medium">Commissions Paid</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Stats Section */}
      <section className="py-12 px-6 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-slate-500 font-medium mb-8">Built for the modern solar sales industry</p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="text-center px-6">
              <p className="text-3xl font-bold text-blue-600">10+</p>
              <p className="text-slate-500 text-sm">Years Industry Experience</p>
            </div>
            <div className="text-center px-6">
              <p className="text-3xl font-bold text-teal-600">24/7</p>
              <p className="text-slate-500 text-sm">Customer Support</p>
            </div>
            <div className="text-center px-6">
              <p className="text-3xl font-bold text-amber-600">99.9%</p>
              <p className="text-slate-500 text-sm">Uptime Guarantee</p>
            </div>
            <div className="text-center px-6">
              <p className="text-3xl font-bold text-purple-600">SSL</p>
              <p className="text-slate-500 text-sm">Encrypted & Secure</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-teal-100 text-teal-700 border-teal-200 px-4 py-2 mb-4 font-medium">
              <Target className="w-4 h-4 mr-2" />
              Features
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Everything You Need to Dominate Solar Sales</h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              From lead capture to commission payout, we have got you covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card 
                key={feature.title}
                className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 group"
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                    <feature.icon className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="relative py-20 px-6 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-slate-50/98 via-white/95 to-white/98" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-2 mb-4 font-medium">
              <Play className="w-4 h-4 mr-2" />
              How It Works
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Up and Running in Minutes</h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Watch our quick tutorials to see how easy it is to get started.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Video Player */}
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shadow-lg">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                  <p className="text-slate-600 font-medium">Click to play demo video</p>
                  <p className="text-slate-400 text-sm mt-1">Coming soon!</p>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              {howItWorks.map((item, idx) => (
                <div 
                  key={item.step}
                  className={`p-6 rounded-xl border transition-all duration-300 cursor-pointer ${
                    activeVideo === idx 
                      ? 'bg-blue-50 border-blue-300 shadow-md' 
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                  onClick={() => setActiveVideo(idx)}
                >
                  <div className="flex items-start gap-4">
                    <div className={`text-4xl font-bold ${activeVideo === idx ? 'text-blue-600' : 'text-slate-300'}`}>
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Your Free Trial Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 px-4 py-2 mb-4 font-medium">
              <Award className="w-4 h-4 mr-2" />
              Why Choose Us
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-slate-900">The Solar CRM Difference</h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Purpose-built tools that understand how solar sales actually work.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="text-center p-8 rounded-2xl bg-slate-50 border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-500 mb-6">Ready to see it in action?</p>
            <Link to="/signup">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Your Free 14-Day Trial
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-2 mb-4 font-medium">
              <DollarSign className="w-4 h-4 mr-2" />
              Simple Pricing
            </Badge>
            <h2 className="text-4xl font-bold mb-4 text-slate-900">One Plan. Everything Included.</h2>
            <p className="text-slate-600 text-lg">No hidden fees. No per-user charges. Unlimited team members.</p>
          </div>

          <Card className="bg-white border-slate-200 shadow-xl overflow-hidden relative">
            <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
              14-Day Free Trial
            </div>
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-5xl font-bold text-slate-900">$349</span>
                    <span className="text-slate-500 text-lg">.99/month</span>
                  </div>
                  <ul className="space-y-3">
                    {[
                      'Unlimited users & team members',
                      'Unlimited leads & deals',
                      'Commission tracking & payouts',
                      '10-stage sales pipeline',
                      'Document storage & uploads',
                      'PDF reports & exports',
                      'Email notifications',
                      'Priority support',
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-slate-700">
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-center">
                  <Link to="/signup">
                    <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg h-auto shadow-lg">
                      <Sparkles className="w-5 h-5 mr-2" />
                      Start Free Trial
                    </Button>
                  </Link>
                  <p className="text-slate-500 text-sm mt-4">No credit card required</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-blue-600/90" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Ready to Become a Solar Mogul?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join hundreds of solar companies already using our platform to close more deals and track every commission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg h-auto shadow-lg">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Your Free 14-Day Trial
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg h-auto">
                Sign In to Your Account
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="Solar Moguls" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-xl font-bold">Solar Moguls</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Privacy Policy</Link>
              <a href="mailto:support@solarmoguls.com" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Solar Moguls. Powered by Pure Solar Systems.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
